import { motion } from "framer-motion";
import {
  FlaskConical,
  Salad,
  Link2,
  HeartPulse,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});

const SEVERITY = {
  severe: { color: "#B33A3A", bg: "#FBECEC", icon: ShieldAlert, label: "Severe" },
  moderate: { color: "#B26B1E", bg: "#FCF1E0", icon: AlertTriangle, label: "Moderate" },
  mild: { color: "#2C4C3B", bg: "#E9F0EC", icon: ShieldCheck, label: "Mild" },
};

function CardShell({ title, eyebrow, Icon, className = "", children, testId }) {
  return (
    <div
      className={`rounded-2xl border p-6 sm:p-8 lg:p-10 lift ${className}`}
      style={{ background: "white", borderColor: "var(--border-default)" }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-5">
        <span
          className="inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0"
          style={{ background: "var(--brand-light)" }}
        >
          <Icon className="h-5 w-5" style={{ color: "var(--brand)" }} strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <p className="label-small">{eyebrow}</p>
          <h3 className="font-serif-display text-xl sm:text-2xl tracking-tight" style={{ color: "var(--text-primary)" }}>
            {title}
          </h3>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function ResultsBento({ result }) {
  const { labs = [], diet = { eat: [], avoid: [] }, interactions = [], organs = [] } = result;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
      {/* Labs */}
      <motion.div {...fade(0)} className="col-span-1 md:col-span-12 lg:col-span-7">
        <CardShell title="Labs to ask your doctor for" eyebrow="01 · Diagnostics" Icon={FlaskConical} testId="labs-card">
          {labs.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No specific labs surfaced.</p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-default)" }}>
              {labs.map((l, i) => (
                <li key={i} className="py-4 first:pt-0 last:pb-0" data-testid={`lab-item-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-medium" style={{ color: "var(--text-primary)" }}>{l.name}</p>
                    {l.frequency && (
                      <span className="label-small" style={{ color: "var(--brand)" }}>{l.frequency}</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{l.why}</p>
                </li>
              ))}
            </ul>
          )}
        </CardShell>
      </motion.div>

      {/* Diet */}
      <motion.div {...fade(0.1)} className="col-span-1 md:col-span-12 lg:col-span-5">
        <CardShell title="On your plate" eyebrow="02 · Diet" Icon={Salad} testId="diet-card">
          <div className="space-y-6">
            <div>
              <p className="label-small mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "var(--brand)" }} /> Lean in
              </p>
              <ul className="space-y-3">
                {(diet.eat || []).map((d, i) => (
                  <li key={i} className="flex gap-3" data-testid={`diet-eat-${i}`}>
                    <span
                      className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ background: "var(--brand)" }}
                    />
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{d.item}</p>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{d.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-6 border-t" style={{ borderColor: "var(--border-default)" }}>
              <p className="label-small mb-3 flex items-center gap-2" style={{ color: "var(--terracotta)" }}>
                <XCircle className="h-3.5 w-3.5" /> Be careful with
              </p>
              <ul className="space-y-3">
                {(diet.avoid || []).map((d, i) => (
                  <li key={i} className="flex gap-3" data-testid={`diet-avoid-${i}`}>
                    <span
                      className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ background: "var(--terracotta)" }}
                    />
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{d.item}</p>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{d.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardShell>
      </motion.div>

      {/* Interactions */}
      <motion.div {...fade(0.2)} className="col-span-1 md:col-span-12 lg:col-span-6">
        <CardShell title="Interactions to watch" eyebrow="03 · Crosswinds" Icon={Link2} testId="interactions-card">
          {interactions.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No notable interactions surfaced.</p>
          ) : (
            <ul className="space-y-4">
              {interactions.map((it, i) => {
                const sev = SEVERITY[it.severity?.toLowerCase()] || SEVERITY.mild;
                const SevIcon = sev.icon;
                const withName = it.with_item || it.with || "—";
                return (
                  <li
                    key={i}
                    className="rounded-xl border p-4"
                    style={{ borderColor: "var(--border-default)" }}
                    data-testid={`interaction-${i}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="font-medium" style={{ color: "var(--text-primary)" }}>
                        <span className="label-small mr-2" style={{ color: "var(--text-muted)" }}>
                          {it.kind || "interaction"}
                        </span>
                        {withName}
                      </p>
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs"
                        style={{ background: sev.bg, color: sev.color }}
                      >
                        <SevIcon className="h-3 w-3" /> {sev.label}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                      {it.description}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </CardShell>
      </motion.div>

      {/* Organs */}
      <motion.div {...fade(0.3)} className="col-span-1 md:col-span-12 lg:col-span-6">
        <CardShell title="Organs doing the work" eyebrow="04 · Body load" Icon={HeartPulse} testId="organs-card">
          {organs.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No specific organ load surfaced.</p>
          ) : (
            <ul className="space-y-5">
              {organs.map((o, i) => (
                <li key={i} className="flex gap-4" data-testid={`organ-${i}`}>
                  <div
                    className="flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center font-serif-display text-lg"
                    style={{ background: "var(--brand-light)", color: "var(--brand)" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: "var(--text-primary)" }}>{o.organ}</p>
                    <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                      <span className="label-small mr-1.5" style={{ color: "var(--terracotta)" }}>Concern</span>
                      {o.concern}
                    </p>
                    <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
                      <span className="label-small mr-1.5" style={{ color: "var(--brand)" }}>Support</span>
                      {o.detox_support}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardShell>
      </motion.div>
    </div>
  );
}
